import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";

const TOPO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";

const MARKETS = {
  Panama: { label: "Panamá", role: "hub", roleLabel: "Hub regional · Logística" },
  Colombia: { label: "Colombia", role: "office", roleLabel: "Oficina regional · Ventas" },
  Guatemala: { label: "Guatemala", role: "office", roleLabel: "Oficina regional" },
  Honduras: { label: "Honduras", role: "dist", roleLabel: "Distribución" },
  "El Salvador": { label: "El Salvador", role: "dist", roleLabel: "Distribución" },
  Nicaragua: { label: "Nicaragua", role: "dist", roleLabel: "Distribución" },
  "Dominican Rep.": { label: "Rep. Dominicana", role: "dist", roleLabel: "Distribución" },
  Bahamas: { label: "Bahamas", role: "dist", roleLabel: "Distribución" },
  "Sint Maarten": { label: "Saint Maarten", role: "dist", roleLabel: "Distribución" },
  "Antigua and Barb.": { label: "Antigua", role: "dist", roleLabel: "Distribución" },
  Barbados: { label: "Barbados", role: "dist", roleLabel: "Distribución" },
  Aruba: { label: "Aruba", role: "dist", roleLabel: "Distribución" },
  "Curaçao": { label: "Curazao", role: "dist", roleLabel: "Distribución" },
};

const ISLAND_DOTS = [
  { name: "Bahamas", coords: [-77.4, 24.7] },
  { name: "Dominican Rep.", coords: [-70.16, 18.74] },
  { name: "Sint Maarten", coords: [-63.05, 18.04] },
  { name: "Antigua and Barb.", coords: [-61.8, 17.08] },
  { name: "Barbados", coords: [-59.55, 13.16] },
  { name: "Aruba", coords: [-69.97, 12.52] },
  { name: "Curaçao", coords: [-68.99, 12.17] },
];

const HUB_COORDS = [-79.7, 8.98];
const OFFICE_COORDS = [
  { name: "Colombia", coords: [-74.1, 4.65] },
  { name: "Guatemala", coords: [-90.5, 14.62] },
];

const FILL = { hub: "#EAB308", office: "#DB2777", dist: "#F9FAFB" };

const Star = ({ x, y, size = 13, fill = "#EAB308" }) => {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? size : size / 2.4;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${x + r * Math.cos(a)},${y + r * Math.sin(a)}`);
  }
  return <polygon points={pts.join(" ")} fill={fill} stroke="#0A0A0A" strokeWidth="1.5" />;
};

const MapLatam = () => {
  const [countries, setCountries] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    fetch(TOPO_URL)
      .then((r) => r.json())
      .then((topo) => setCountries(feature(topo, topo.objects.countries).features));
  }, []);

  const { path, projection } = useMemo(() => {
    if (!countries) return {};
    const highlighted = { type: "FeatureCollection", features: countries.filter((f) => MARKETS[f.properties.name]) };
    const projection = geoMercator().fitExtent([[50, 50], [850, 570]], highlighted);
    return { path: geoPath(projection), projection };
  }, [countries]);

  const showTip = (e, name) => {
    const m = MARKETS[name];
    if (!m || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: m.label, roleLabel: m.roleLabel, role: m.role });
  };

  return (
    <div ref={wrapRef} className="relative bg-[#0A0A0A] overflow-hidden rounded-sm" data-testid="latam-map">
      {!countries && (
        <div className="h-[420px] lg:h-[560px] flex items-center justify-center text-white/40 text-xs uppercase tracking-[0.3em]" data-testid="map-loading">
          Cargando mapa…
        </div>
      )}
      {countries && path && (
        <svg viewBox="0 0 900 620" className="w-full h-auto block">
          {countries.map((f, i) => {
            const m = MARKETS[f.properties.name];
            return (
              <path
                key={i}
                d={path(f)}
                fill={m ? FILL[m.role] : "#1d1d1d"}
                stroke={m ? "#0A0A0A" : "#2b2b2b"}
                strokeWidth={m ? 0.8 : 0.5}
                className={m ? "cursor-pointer transition-opacity duration-200 hover:opacity-75" : ""}
                data-testid={m ? `map-country-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}` : undefined}
                onMouseMove={(e) => showTip(e, f.properties.name)}
                onMouseLeave={() => setTooltip(null)}
              />
            );
          })}
          {ISLAND_DOTS.map((d) => {
            const [cx, cy] = projection(d.coords);
            return (
              <circle
                key={d.name}
                cx={cx}
                cy={cy}
                r="6"
                fill="#F9FAFB"
                stroke="#0A0A0A"
                strokeWidth="1.5"
                className="cursor-pointer transition-opacity duration-200 hover:opacity-75"
                onMouseMove={(e) => showTip(e, d.name)}
                onMouseLeave={() => setTooltip(null)}
              />
            );
          })}
          {OFFICE_COORDS.map((o) => {
            const [cx, cy] = projection(o.coords);
            return (
              <g key={o.name} className="pointer-events-none">
                <circle cx={cx} cy={cy} r="10" fill="none" stroke="#DB2777" strokeWidth="2" />
                <circle cx={cx} cy={cy} r="4" fill="#DB2777" stroke="#0A0A0A" strokeWidth="1" />
              </g>
            );
          })}
          {(() => {
            const [sx, sy] = projection(HUB_COORDS);
            return (
              <g className="pointer-events-none">
                <circle cx={sx} cy={sy} r="20" fill="#EAB308" opacity="0.18" />
                <Star x={sx} y={sy} />
              </g>
            );
          })()}
        </svg>
      )}

      {tooltip && (
        <div
          className="absolute z-10 pointer-events-none bg-white text-[#0A0A0A] px-4 py-2.5 shadow-xl"
          style={{ left: Math.min(tooltip.x + 16, (wrapRef.current?.clientWidth || 600) - 190), top: tooltip.y - 56 }}
          data-testid="map-tooltip"
        >
          <p className="font-display font-bold uppercase tracking-tight text-sm leading-none">{tooltip.label}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold mt-1.5" style={{ color: tooltip.role === "dist" ? "#4B5563" : FILL[tooltip.role] }}>
            {tooltip.roleLabel}
          </p>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 flex flex-wrap gap-x-8 gap-y-2 px-6 lg:px-8 py-4 border-t border-white/10 bg-[#0A0A0A]/80 backdrop-blur-sm" data-testid="map-legend">
        <span className="flex items-center gap-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">
          <svg width="16" height="16" viewBox="0 0 28 28"><Star x={14} y={14} size={11} /></svg>
          Hub regional — Panamá
        </span>
        <span className="flex items-center gap-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">
          <span className="w-3 h-3 rounded-full border-2 border-[#DB2777] inline-block" />
          Oficinas regionales — Colombia &amp; Guatemala
        </span>
        <span className="flex items-center gap-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">
          <span className="w-3 h-3 bg-[#F9FAFB] inline-block" />
          Mercados con distribución
        </span>
      </div>
    </div>
  );
};

export default MapLatam;
